import { useTranslator } from '@bluebeam/react-i18n';
import turfBBox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import turfCenterOfMass from '@turf/center-of-mass';
import { polygon } from '@turf/helpers';
import CroppedSheet, { MapboxSheetLayerConfig } from 'components/CroppedSheet';
import MapLayers from 'components/Icons/MapLayers';
import SearchInputResults from 'components/inputs/SearchInputResults';
import { Button } from 'components/lib';
import MapIconButton from 'components/MapIconButton';
import MapViewTypeToggle from 'components/MapViewTypeToggle';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import Tooltip from 'components/Tooltip';
import TooltipButton from 'components/Tooltip/TooltipButton';
import ViewMapLayers from 'components/ViewMapLayers';
import ZoomInOut from 'components/ZoomInOut/ZoomInOut';
import { Direction, Directions } from 'constants/directions';
import { MapboxStyles } from 'constants/map';
import { environmentVars } from 'constants/services';
import AppStoreContext from 'contexts/AppStoreContext';
import convert from 'convert-units';
import { isNil } from 'lodash';
import { useMapLayersManager } from 'providers/MapLayersManagerProvider';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactMapGL, {
  FlyToInterpolator,
  Layer,
  LinearInterpolator,
  MapEvent as MapboxPointerEvent,
  MapRef,
  Marker,
  Source,
  WebMercatorViewport,
} from 'react-map-gl';
import { GeoLocation, RawGeoLocation } from 'records/GeoLocation';
import { Scale } from 'records/MappedSheetVersion';
import { searchGeolocation } from 'services/mapService';
import { geoLocationToStringAddress, getProjectViewport } from 'utils/map';
import {
  calculateCornerCoordinates,
  createCoordinateCalculator,
} from 'utils/sheetCalibration';
import AlignRotation from '../AlignRotation/AlignRotation';
import BackButton from '../BackButton';
import {
  CalibrationMachineEventsEnum,
  useCalibrationMachine,
} from '../CalibrationMachine';
import { CalibrationMachineContext } from '../CalibrationMachine/calibrationMachine';
import {
  defaultFlyAnimationDuration,
  maxZoomLevel,
  minZoomLevel,
} from '../constants';
import DirectionalPad from './DirectionalPad';
import SheetAlignerLayers from './SheetAlignerLayers';

const mapContainerId = 'aligner-map-container';

type TransitionableViewport = Viewport & {
  transitionDuration?: number;
  transitionInterpolator?: FlyToInterpolator;
};

const defaultViewport: Viewport = {
  latitude: 0,
  longitude: 0,
  zoom: 18,
  pitch: 0,
  bearing: 0,
};

const transparentStyle = {
  version: 8,
  sources: {},
  layers: [],
};

const defaultCursor = 'grab';
const HEADER_HEIGHT = 250;

const createViewportProjectors = (
  ...viewports: Viewport[]
): WebMercatorViewport[] => {
  const mapContainer = document.getElementById(mapContainerId);
  const height = mapContainer ? mapContainer.offsetHeight : window.innerHeight;
  const width = mapContainer ? mapContainer.offsetWidth : window.innerWidth;

  return viewports.map(
    (viewport) =>
      new WebMercatorViewport({
        ...viewport,
        height,
        width,
      })
  );
};

const projectCoordinatesBetweenViewports: (
  sourceViewportProjector: WebMercatorViewport,
  destinationViewportProjector: WebMercatorViewport,
  coordinates: Coordinate[]
) => Coordinate[] = (
  sourceViewportProjector,
  destinationViewportProjector,
  coordinates
) =>
  coordinates.map((coordinate) => {
    const pixelCoordinate = sourceViewportProjector.project([
      coordinate.longitude,
      coordinate.latitude,
    ]);

    const [longitude, latitude] =
      destinationViewportProjector.unproject(pixelCoordinate);

    return {
      longitude,
      latitude,
    };
  });

const SheetAligner: React.FC = () => {
  const { actions, state: appState } = useContext(AppStoreContext);

  const [isLoading, setIsLoading] = useState(true);
  const [geoLocationResults, setGeoLocationResults] = useState<GeoLocation[]>();
  const [hasError, setError] = useState(false);

  const { mappedSheetQuery, queriedMappedSheets } = appState;

  const sheetMapRef = useRef<MapRef>(null);

  const { calibrationMachineService, calibrationMachineState } =
    useCalibrationMachine();

  const { sheetVersion, calibrationConfig, pageUrl } =
    calibrationMachineState.context;

  const {
    scale,
    croppath,
    bearing: initialBearing,
    centerpoint,
  } = calibrationConfig as CalibrationMachineContext['calibrationConfig'] & {
    scale: Scale;
  };

  const { geoLocation } = appState;
  const mapLayersManager = useMapLayersManager();
  const currentMappedSheets = mapLayersManager.getCurrentMappedSheetEntries();

  const { translate } = useTranslator(['mapView', 'buttons']);

  const calculatedViewPort = getProjectViewport(
    geoLocation,
    currentMappedSheets,
    HEADER_HEIGHT
  );

  const initialMapViewport: Viewport = {
    ...centerpoint,
    ...calculatedViewPort,
    zoom: defaultViewport.zoom,
  };

  const initialSheetViewport: Viewport = {
    ...calculatedViewPort,
    ...centerpoint,
    zoom: defaultViewport.zoom,
  };

  const [sheetViewport, setSheetViewport] = useState<TransitionableViewport>({
    ...initialSheetViewport,
    bearing: initialBearing,
  });
  const [mapViewport, setMapViewport] =
    useState<TransitionableViewport>(initialMapViewport);
  const [isMovingSheet, setIsMovingSheet] = useState<boolean>(false);
  const [cursor, setCursor] = useState<string>(defaultCursor);
  const [sheetBearing, setSheetBearing] = useState<string>(
    sheetViewport.bearing.toString()
  );
  const [mapStyle, setMapStyle] = useState<MapboxStyles>(MapboxStyles.street);
  const [showMapViewToggle, setShowMapViewToggle] = useState<boolean>(false);

  const [showViewMapLayers, setShowViewMapLayers] = useState<boolean>(false);

  const buttonLeftShift = showViewMapLayers ? 477 : 0;

  const showMarkerMaxZoom = 17;

  const [imageCorners, croppathCenter, croppathBBoxCoordinates] =
    useMemo(() => {
      const pdfInches = convert(scale.from.value)
        .from(scale.from.unit)
        .to('in');
      const realFeet = convert(scale.to.value).from(scale.to.unit).to('ft');
      const realFeetPerPaperInch = realFeet / pdfInches;

      const imageCorners = calculateCornerCoordinates({
        center: centerpoint,
        pdfWidth: sheetVersion.width,
        pdfHeight: sheetVersion.height,
        realFeetPerPaperInch,
      });

      const coordinateCalculator = createCoordinateCalculator(
        imageCorners.topleft,
        sheetVersion.width,
        sheetVersion.height,
        realFeetPerPaperInch
      );

      const croppathCoordinates = croppath
        .map(({ x, y }) => coordinateCalculator(x, y))
        .map((point) => point.geometry.coordinates);

      const croppathPolygon = polygon([croppathCoordinates]);
      const bbox = turfBBox(croppathPolygon);
      const croppathPolygonBBox = bboxPolygon(bbox);
      const croppathBBoxCoordinates =
        croppathPolygonBBox.geometry.coordinates[0].slice(0, -1);
      const croppathCenter = turfCenterOfMass(croppathPolygonBBox);

      return [
        imageCorners,
        croppathCenter.geometry.coordinates,
        croppathBBoxCoordinates,
      ];
    }, []);

  const { topleft, topright, bottomright, bottomleft } = imageCorners;

  const imageSourceCorners = [
    [topleft.longitude, topleft.latitude],
    [topright.longitude, topright.latitude],
    [bottomright.longitude, bottomright.latitude],
    [bottomleft.longitude, bottomleft.latitude],
  ];

  const sheetSource = {
    id: 'sheet-aligner-source',
    coordinates: imageSourceCorners,
    type: 'image',
    url: pageUrl,
  };

  const sheetLayer: MapboxSheetLayerConfig = {
    id: 'sheet-aligner-layer',
    type: 'raster',
    source: sheetSource.id,
  };

  const getAlignmentConfig = () => {
    const [sheetViewportProjector, mapViewportProjector] =
      createViewportProjectors(sheetViewport, mapViewport);

    const sheetCenter = {
      longitude: initialSheetViewport.longitude,
      latitude: initialSheetViewport.latitude,
    };

    const [
      alignmentTopLeft,
      alignmentTopRight,
      alignmentBottomLeft,
      alignmentBottomRight,
      alignmentCenter,
    ] = projectCoordinatesBetweenViewports(
      sheetViewportProjector,
      mapViewportProjector,
      [
        imageCorners.topleft,
        imageCorners.topright,
        imageCorners.bottomleft,
        imageCorners.bottomright,
        sheetCenter,
      ]
    );

    return {
      corners: {
        topleft: alignmentTopLeft,
        topright: alignmentTopRight,
        bottomleft: alignmentBottomLeft,
        bottomright: alignmentBottomRight,
      },
      bearing: sheetViewport.bearing,
      centerpoint: alignmentCenter,
    };
  };

  const onMouseMove = (event: MapboxPointerEvent) => {
    if (!sheetMapRef.current) {
      return;
    }

    const isHoveringOverSheet =
      sheetMapRef.current.queryRenderedFeatures(event.point).length > 0;

    setCursor(isHoveringOverSheet ? 'move' : defaultCursor);
  };

  const onMouseDown = (event: MapboxPointerEvent) => {
    if (!sheetMapRef.current) {
      return;
    }

    const isSheetClick =
      sheetMapRef.current.queryRenderedFeatures(event.point).length > 0;

    if (isSheetClick) {
      setIsMovingSheet(true);
    }
  };

  const onMouseUp = () => {
    setIsMovingSheet(false);
  };

  const moveSheet = (updatedViewport: Viewport) => {
    setSheetBearing(updatedViewport.bearing.toString());

    setSheetViewport({
      ...updatedViewport,
      pitch: defaultViewport.pitch,
    });

    setMapViewport((prev) => ({
      ...prev,
      zoom: updatedViewport.zoom,
    }));
  };

  const moveSheetAndMapTogether = (updatedViewport: Viewport) => {
    const [sheetViewportProjector, mapViewportProjector] =
      createViewportProjectors(sheetViewport, mapViewport);

    const prevSheetCenterPixels = sheetViewportProjector.project([
      sheetViewport.longitude,
      sheetViewport.latitude,
    ]);

    const newSheetCenterPixels = sheetViewportProjector.project([
      updatedViewport.longitude,
      updatedViewport.latitude,
    ]);

    const horizontalDelta = newSheetCenterPixels[0] - prevSheetCenterPixels[0];
    const verticalDelta = newSheetCenterPixels[1] - prevSheetCenterPixels[1];

    const prevMapCenterPixels = mapViewportProjector.project([
      mapViewport.longitude,
      mapViewport.latitude,
    ]);

    const [newMapCenterLongitude, newMapCenterLatitude] =
      mapViewportProjector.unproject([
        prevMapCenterPixels[0] + horizontalDelta,
        prevMapCenterPixels[1] + verticalDelta,
      ]);

    setSheetViewport({
      ...updatedViewport,
      pitch: defaultViewport.pitch,
    });

    setMapViewport({
      ...defaultViewport,
      latitude: newMapCenterLatitude,
      longitude: newMapCenterLongitude,
      zoom: updatedViewport.zoom,
    });
  };

  const updateViewports = (updatedViewport: Viewport) => {
    if (isMovingSheet) {
      moveSheet(updatedViewport);
    } else {
      moveSheetAndMapTogether(updatedViewport);
    }
  };

  const applyRotationValues = (bearing: number) => {
    setSheetBearing(bearing.toString());

    setSheetViewport((prev) => ({
      ...prev,
      bearing,
    }));
  };

  const rotateSheet = (direction: string) => () => {
    const { bearing } = sheetViewport;
    let newBearing = bearing;

    if (direction === 'left') {
      newBearing = bearing + 0.1;
    }

    if (direction === 'right') {
      newBearing = bearing - 0.1;
    }

    applyRotationValues(parseFloat(newBearing.toFixed(5)));
  };

  const sanitizeInput = (inputValue: number) => {
    if (inputValue < -180 || inputValue > 180) {
      const inRange = inputValue % 360;
      const sign = inRange < 180 ? inRange : inRange - 360;
      return sign;
    }

    return inputValue;
  };

  const rotateSheetInput = (degrees: string) => {
    if (degrees.endsWith('.') || degrees === '-' || degrees === '') {
      setSheetBearing(degrees);
    } else if (/^(\d|-|\.)+$/.test(degrees)) {
      const newBearing = parseFloat(degrees);
      const sanitizedBearing = sanitizeInput(newBearing);
      applyRotationValues(parseFloat(sanitizedBearing.toFixed(5)));
    }
  };

  const nudge = (direction: Direction) => () => {
    const [sheetViewportProjector] = createViewportProjectors(sheetViewport);

    const [oldCenterX, oldCenterY] = sheetViewportProjector.project([
      sheetViewport.longitude,
      sheetViewport.latitude,
    ]);

    const nudgeAmount = 1;
    let newCenterX = oldCenterX;
    let newCenterY = oldCenterY;

    switch (direction) {
      case Directions.UP:
        newCenterY = oldCenterY + nudgeAmount;
        break;
      case Directions.DOWN:
        newCenterY = oldCenterY - nudgeAmount;
        break;
      case Directions.LEFT:
        newCenterX = oldCenterX + nudgeAmount;
        break;
      case Directions.RIGHT:
        newCenterX = oldCenterX - nudgeAmount;
        break;
      default:
    }

    const [newLongitude, newLatitude] = sheetViewportProjector.unproject([
      newCenterX,
      newCenterY,
    ]);

    setSheetViewport((prev) => ({
      ...prev,
      longitude: newLongitude,
      latitude: newLatitude,
    }));
  };

  const getCursor = cursor === defaultCursor ? undefined : () => cursor;

  const flyToSheet = () => {
    const [sheetViewportProjector, mapViewportProjector] =
      createViewportProjectors(sheetViewport, mapViewport);

    const [sheetCenterOnMap] = projectCoordinatesBetweenViewports(
      sheetViewportProjector,
      mapViewportProjector,
      [
        {
          longitude: initialSheetViewport.longitude,
          latitude: initialSheetViewport.latitude,
        },
      ]
    );

    setSheetViewport((prev) => ({
      ...prev,
      longitude: initialSheetViewport.longitude,
      latitude: initialSheetViewport.latitude,
      transitionDuration: defaultFlyAnimationDuration,
      transitionInterpolator: new FlyToInterpolator(),
    }));

    setMapViewport((prev) => ({
      ...prev,
      ...sheetCenterOnMap,
      transitionDuration: defaultFlyAnimationDuration,
      transitionInterpolator: new FlyToInterpolator(),
    }));
  };

  const zoomMap = (zoomIn: boolean) => {
    const zoomIncrement = zoomIn ? 1 : -1;
    let newZoom = mapViewport.zoom + zoomIncrement;

    if (newZoom <= minZoomLevel) {
      newZoom = minZoomLevel;
    }

    setSheetViewport((prev) => ({
      ...prev,
      zoom: newZoom,
      transitionDuration: defaultFlyAnimationDuration,
      transitionInterpolator: new LinearInterpolator(),
    }));

    setMapViewport((prev) => ({
      ...prev,
      zoom: newZoom,
      transitionDuration: defaultFlyAnimationDuration,
      transitionInterpolator: new LinearInterpolator(),
    }));
  };

  const zoomMapScroll = (event: MapboxPointerEvent) => {
    if (event.delta) {
      const zoomIncrement = event.delta > 0 ? 1 : -1;
      let newZoom = mapViewport.zoom + zoomIncrement;

      if (newZoom <= minZoomLevel) {
        newZoom = minZoomLevel;
      }

      setSheetViewport((prev) => ({
        ...prev,
        zoom: newZoom,
        transitionDuration: defaultFlyAnimationDuration,
        transitionInterpolator: new LinearInterpolator(),
      }));

      setMapViewport((prev) => ({
        ...prev,
        zoom: newZoom,
        transitionDuration: defaultFlyAnimationDuration,
        transitionInterpolator: new LinearInterpolator(),
      }));
    }
  };

  const showMarker = () => {
    return mapViewport.zoom <= showMarkerMaxZoom;
  };

  const handleSearchChange = async (searchString: string) => {
    try {
      setIsLoading(true);
      const emptyResults: GeoLocation[] = [];
      const searchLocations = await searchGeolocation(searchString);

      if (!isNil(searchLocations)) {
        setGeoLocationResults(searchLocations);
      } else {
        setGeoLocationResults(emptyResults);
      }

      setIsLoading(false);
    } catch (error) {
      setError(true);
    }
  };

  const handleSelect = (selectedLocation: RawGeoLocation) => {
    const newMapCenterLatitude = selectedLocation.center[1];
    const newMapCenterLongitude = selectedLocation.center[0];

    setMapViewport({
      ...defaultViewport,
      latitude: newMapCenterLatitude,
      longitude: newMapCenterLongitude,
    });

    moveSheet({
      ...defaultViewport,
      ...centerpoint,
    });
  };

  const isFirstTimeOpen = appState.getCurrentMappedSheetEntries().size === 0;

  const [selectedMapAddress] = useState(
    geoLocationToStringAddress(geoLocation)
  );
  const [defaultGeoLocationValue] = useState(
    isFirstTimeOpen ? selectedMapAddress : ''
  );

  useEffect(() => {
    if (isFirstTimeOpen) {
      handleSearchChange(`${selectedMapAddress}`);
    }
  }, [selectedMapAddress]);

  return (
    <div className="w100 h100 column">
      <div className="row mx3 my2 justify-between items-center">
        <div className="font-primary font-size-5 black-2 row items-center">
          <BackButton
            onClick={() => {
              calibrationMachineService.send({
                type: CalibrationMachineEventsEnum.BACK_TO_CROP,
                data: getAlignmentConfig(),
              });
            }}
          />
          <span className="medium mr1">
            {translate('mapView:sheetAligner.stepThreeOfThree')}{' '}
          </span>
          {translate('mapView:sheetAligner.placeDrawingOnMap')}
          <Tooltip
            title={translate('mapView:sheetAligner.placementDetails')}
            content={
              <>
                <p className="font-size-4 normal mb3">
                  {translate('mapView:sheetAligner.placementDetailsTextOne')}
                </p>
                <p className="font-size-4 normal m0">
                  {translate('mapView:sheetAligner.placementDetailsTextTwo')}
                </p>
              </>
            }
          >
            <TooltipButton variant="info" />
          </Tooltip>
        </div>
        <div className="row">
          <Button
            id="map-prepare-align-cancel-btn"
            variant="tertiary"
            className="mr3"
            onClick={actions.clearAllModals}
          >
            {translate('buttons:cancel')}
          </Button>
          <Button
            id="map-prepare-align-save-btn"
            variant="primary"
            onClick={() => {
              calibrationMachineService.send({
                type: CalibrationMachineEventsEnum.SAVE_CALIBRATION,
                data: getAlignmentConfig(),
              });
            }}
          >
            {translate('buttons:save')}
          </Button>
        </div>
      </div>
      <div className="h100 w100 pos-r">
        <div id={mapContainerId} className="h100 w100">
          <ReactMapGL
            {...mapViewport}
            height="100%"
            mapStyle={mapStyle}
            mapboxApiAccessToken={environmentVars.MAPBOX_API_KEY}
            maxPitch={0}
            maxZoom={maxZoomLevel}
            minZoom={minZoomLevel}
            width="100%"
          >
            <SheetAlignerLayers />
          </ReactMapGL>
        </div>
        <div
          className="pos-a h100 w100"
          style={{
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <ReactMapGL
            {...sheetViewport}
            attributionControl={false}
            getCursor={getCursor}
            height="100%"
            mapboxApiAccessToken={environmentVars.MAPBOX_API_KEY}
            mapStyle={transparentStyle}
            maxPitch={0}
            maxZoom={maxZoomLevel}
            minZoom={minZoomLevel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onWheel={zoomMapScroll}
            scrollZoom={false}
            onViewportChange={updateViewports}
            ref={sheetMapRef}
            width="100%"
          >
            {showMarker() ? (
              <Marker
                longitude={croppathCenter[0]}
                latitude={croppathCenter[1]}
                offsetLeft={-30}
                offsetTop={-50}
                captureDrag={false}
              >
                <div
                  className="marker"
                  onMouseDown={() => setIsMovingSheet(true)}
                />
              </Marker>
            ) : (
              <>
                <Source
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: {
                      type: 'Polygon',
                      coordinates: [
                        [...imageSourceCorners, imageSourceCorners[0]],
                      ],
                    },
                    properties: {},
                  }}
                >
                  <CroppedSheet
                    cropPath={croppath}
                    sheetLayer={sheetLayer}
                    sheetSource={sheetSource}
                    sheetVersion={sheetVersion}
                  />
                </Source>
                <Source
                  type="geojson"
                  data={{
                    type: 'Feature',
                    geometry: {
                      type: 'Polygon',
                      coordinates: [
                        [
                          ...croppathBBoxCoordinates,
                          croppathBBoxCoordinates[0],
                        ],
                      ],
                    },
                    properties: {},
                  }}
                >
                  <Layer
                    id="sheet-image-background-line-border"
                    type="line"
                    paint={{
                      'line-width': 2,
                      'line-color': '#0074cc',
                      'line-dasharray': [2, 2],
                      'line-offset': -6,
                    }}
                  />
                  <Layer
                    id="sheet-image-background-line-fill"
                    type="fill"
                    paint={{ 'fill-color': 'rgba(0, 0, 0, 0.01)' }}
                  />
                </Source>
              </>
            )}
          </ReactMapGL>
          {showViewMapLayers && (
            <OverlayStdContainer
              id="view-map-layers-overlay"
              style={{
                right: 'initial',
                top: '0',
                height: '100%',
                zIndex: 1,
              }}
            >
              <ViewMapLayers
                allowItemEdit={false}
                launchDrawingSelectionModal={() => {}}
                onCancelClick={() => setShowViewMapLayers(false)}
                queriedMappedSheetsCount={queriedMappedSheets.count()}
                startingSortField={mappedSheetQuery.sortField}
                startingSortDirection={mappedSheetQuery.sortDirection}
              />
            </OverlayStdContainer>
          )}
          <div
            className="pos-a map-icon-button-wrapper"
            style={{
              top: '16px',
              left: '16px',
              boxShadow: '0 2px 16px 0 rgba(0, 116, 204, 0.08)',
            }}
          >
            <MapIconButton
              disabled={appState.getCurrentMappedSheetEntries().size === 0}
              onClick={() => setShowViewMapLayers(!showViewMapLayers)}
            >
              <MapLayers height="24" />
            </MapIconButton>
          </div>

          <div
            className="pos-a p2"
            style={{
              top: '16px',
              left: '75px',
            }}
          >
            <SearchInputResults
              id="project-location-search"
              placeholder={translate('mapView:searchInput.search')}
              defaultValue={defaultGeoLocationValue}
              onChange={handleSearchChange}
              onSelect={handleSelect}
              autoSearchResults={false}
              results={geoLocationResults}
              isLoading={isLoading}
              hasError={hasError}
              style={{
                height: '32px',
                maxWidth: '210px',
                padding: '4px 8px',
                fontSize: '14px',
              }}
            />
          </div>

          <ZoomInOut
            id="map-prepare-align"
            disableZoomIn={mapViewport.zoom === maxZoomLevel}
            disableZoomOut={mapViewport.zoom <= minZoomLevel}
            handleZoomIn={() => zoomMap(true)}
            handleZoomOut={() => zoomMap(false)}
            style={{ left: buttonLeftShift + 17 }}
          />

          <MapViewTypeToggle
            id="map-layer-align-view"
            mapStyle={mapStyle}
            onStyleChange={setMapStyle}
            mapPosition={{ bottom: 32, left: buttonLeftShift + 78 }}
            showMenu={showMapViewToggle}
            toggleMenu={() => setShowMapViewToggle(!showMapViewToggle)}
          />

          <div
            className="pos-a"
            style={{
              bottom: '92px',
              right: '30px',
            }}
          >
            <DirectionalPad
              onUpClick={nudge(Directions.UP)}
              onDownClick={nudge(Directions.DOWN)}
              onLeftClick={nudge(Directions.LEFT)}
              onRightClick={nudge(Directions.RIGHT)}
              onCenterClick={flyToSheet}
            />
          </div>
          <AlignRotation
            rotateSheet={rotateSheet}
            rotateSheetInput={rotateSheetInput}
            sheetBearing={sheetBearing}
          />
        </div>
      </div>
    </div>
  );
};

export default SheetAligner;
